import type { QueryClient } from '@tanstack/react-query';
import type { CampaignWhatIfQuery, ListRunsQuery } from '@clarity/contract';
import { tsr } from '../lib/api.js';
import { isStoreBannerOptionsKey, queryKeys } from './keys.js';

// Admin subscription + invoice hooks live in their own module (keyed off the
// shared `queryKeys` registry, which lives in `./keys.ts` so there is no
// `index ↔ subscriptions` import cycle); re-export so they are reachable like
// the other hooks (clarity-subscriptions Req 15.1, 15.3, 15.6).
export * from './subscriptions.js';

// The shared query-key registry lives in its own leaf module (imported above);
// re-export it so callers keep reaching `queryKeys` from `../queries/index.js`.
export { isStoreBannerOptionsKey, queryKeys };

/**
 * Typed query/mutation hooks over the ts-rest react-query client (Req 6.5).
 *
 * Every hook here delegates to `tsr.<resource>.<route>.useQuery/useMutation`, so
 * loading, error and caching are owned by the query layer — components read
 * `isLoading` / `error` / `data` and never touch `fetch`. Mutations invalidate
 * the lists they affect via the ts-rest query client so the UI reflects writes
 * without manual refetching.
 *
 * `data` from a ts-rest hook is a discriminated `{ status, body }` union; callers
 * narrow on `status === 200/201` to reach the typed body.
 */

// ── Reads ──────────────────────────────────────────────────────────────────

/** List clients (admin: all; server scopes a client to its own). */
export function useClients() {
  return tsr.clients.list.useQuery({ queryKey: queryKeys.clients });
}

/** Read ONE client by id, tenant-scoped (admin reads any; a client only its own). */
export function useClient(id: string, enabled = true) {
  return tsr.clients.getById.useQuery({
    queryKey: queryKeys.client(id),
    queryData: { params: { id } },
    enabled,
  });
}

/**
 * List the per-platform payment-gateway fee defaults (onboarding Req 4.1).
 * One entry per platform (unset where none is stored). Admin-only server-side;
 * also read by onboarding Step 1 to seed the fee form for a fresh store.
 */
export function usePlatformFeeDefaults() {
  return tsr.settings.listFeeDefaults.useQuery({ queryKey: queryKeys.platformFeeDefaults });
}

/** List brands visible to the caller (tenant-scoped server-side). */
export function useBrands() {
  return tsr.brands.list.useQuery({ queryKey: queryKeys.brands });
}

/** List a single client's brands (hierarchy view). */
export function useBrandsByClient(clientId: string, enabled = true) {
  return tsr.brands.listByClient.useQuery({
    queryKey: queryKeys.brandsByClient(clientId),
    queryData: { params: { clientId } },
    enabled,
  });
}

/** List stores (branches) visible to the caller. */
export function useStores() {
  return tsr.stores.list.useQuery({ queryKey: queryKeys.stores });
}

/**
 * List one brand's branches (the brand → branches hierarchy; Req 3.1, 6.1).
 * Wraps `stores.listByBrand` (`GET /brands/:brandId/stores`), tenant-scoped
 * server-side. Mirrors {@link useBrandsByClient}: a parameterized query key so a
 * brand-scoped view caches independently, and `enabled` gates the fetch while a
 * brand is unresolved. The combined brand-scope surfaces read in-scope branches
 * through this hook.
 */
export function useStoresByBrand(brandId: string, enabled = true) {
  return tsr.stores.listByBrand.useQuery({
    queryKey: queryKeys.storesByBrand(brandId),
    queryData: { params: { brandId } },
    enabled,
  });
}

/** Read a single store incl. its lifecycle state (Req 3.5, 6.4). */
export function useStore(id: string, enabled = true) {
  return tsr.stores.getById.useQuery({
    queryKey: queryKeys.store(id),
    queryData: { params: { id } },
    enabled,
  });
}

// ── Ingested-data reads (clarity-onboarding-ingestion Req 14.2, 10.2) ─────────
//
// All tenant-scoped to one store. The not-yet-onboarded block (task 5.2) means a
// store that is `assigned` and not onboarded answers these with `409`
// (`code: 'onboarding_required'`); the UI gates on `store.state` +
// `store.onboardingComplete` (the store read) so it only mounts these for an
// onboarded store, and the hooks surface the `409` body gracefully otherwise.

/** List a store's normalized orders (`enabled` gates the fetch while gated). */
export function useOrders(storeId: string, enabled = true) {
  return tsr.orders.list.useQuery({
    queryKey: queryKeys.orders(storeId),
    queryData: { params: { id: storeId }, query: {} },
    enabled,
  });
}

/** Read one order's detail (line items, discounts, fees, payout, provenance; Req 10.2). */
export function useOrder(storeId: string, orderId: string, enabled = true) {
  return tsr.orders.getById.useQuery({
    queryKey: queryKeys.order(storeId, orderId),
    queryData: { params: { id: storeId, orderId } },
    enabled,
  });
}

/** List a store's merged per-period statements. */
export function useStatements(storeId: string, enabled = true) {
  return tsr.statements.list.useQuery({
    queryKey: queryKeys.statements(storeId),
    queryData: { params: { id: storeId } },
    enabled,
  });
}

/** List a store's normalized payouts. */
export function usePayouts(storeId: string, enabled = true) {
  return tsr.payouts.list.useQuery({
    queryKey: queryKeys.payouts(storeId),
    queryData: { params: { id: storeId } },
    enabled,
  });
}

// ── Campaign reads (clarity-campaign-matching Req 9.1, 10.1) ──────────────────
//
// All tenant-scoped to one store (the Campaigns surface is per-store). Like the
// orders/payouts reads, a not-yet-onboarded store answers these with `409`
// (`code: 'onboarding_required'`); the nav-level onboarding lock keeps the
// surface off-limits until onboarded, and the hooks surface the `409` body
// gracefully as defense-in-depth. `data` is the ts-rest `{ status, body }`
// union — callers narrow on `status === 200` to reach the typed body.

/**
 * List a store's campaigns + the period roll-up headline (Req 9.1, 9.2). The
 * list leads with the headline strip (orders matched/total, campaign-driven
 * sales, total ad+promo cost + the payout-ads tie, blended ROAS) and the
 * per-campaign rows (each campaign + its folded actuals + the projection for the
 * actual-vs-projected bar). `enabled` gates the fetch while the surface is gated.
 */
export function useCampaigns(storeId: string, enabled = true) {
  return tsr.campaigns.list.useQuery({
    queryKey: queryKeys.campaigns(storeId),
    queryData: { params: { id: storeId } },
    enabled,
  });
}

/**
 * Read ONE campaign's detail — the forward projection + occurrences (with
 * inferred participation) + actuals (Req 9.3). The optional `whatIf`
 * (`discountPct`/`cap`/`cpc`) recomputes the SAME pure `projectCampaign` on the
 * server by shallow-overriding the campaign's declared terms; co-funding is NOT
 * a lever (pinned, Req 7.3), so there is no `coFund` key. Each what-if caches
 * independently (the key folds the overrides), so dragging a slider doesn't
 * clobber the default projection. Used by the detail view (task 13.2).
 */
export function useCampaign(
  storeId: string,
  campaignId: string,
  whatIf?: CampaignWhatIfQuery,
  enabled = true,
) {
  return tsr.campaigns.detail.useQuery({
    queryKey: queryKeys.campaign(storeId, campaignId, whatIf),
    queryData: { params: { id: storeId, cid: campaignId }, query: whatIf ?? {} },
    enabled,
  });
}

/**
 * Read ONE occurrence's detail — the override terms + its actuals + the
 * matched-order evidence rows (Req 9.4). Used by the occurrence view (task 13.4).
 */
export function useOccurrence(
  storeId: string,
  campaignId: string,
  occurrenceId: string,
  enabled = true,
) {
  return tsr.campaigns.occurrence.useQuery({
    queryKey: queryKeys.campaignOccurrence(storeId, campaignId, occurrenceId),
    queryData: {
      params: { id: storeId, cid: campaignId, oid: occurrenceId },
    },
    enabled,
  });
}

// ── Reconciliation reads (clarity-reconciliation Req 8.4) ─────────────────────
//
// Both resolve the LATEST report from the append-only report store (a
// re-reconcile appends a new version; the read side surfaces the newest).
// A `404` is EXPECTED and not an error state: it means "no report for this
// scope yet" (the payout/period is not reconciled). ts-rest surfaces that `404`
// on `query.error` as `{ status: 404, body }`, so callers treat `errorStatus
// === 404` as "not reconciled yet" and only `isError` without a status (a real
// transport failure) as a failure. The not-yet-onboarded `409` is handled the
// same way the orders/payouts reads handle it.

/** Latest reconciliation report for one payout; `404` ⇒ not reconciled yet. */
/**
 * Read the campaign RECONCILIATION for a store over a date range
 * (clarity-campaign-reconciliation Req 4): our matched actuals vs the platform's
 * reported per-campaign performance, per campaign + the store-period total.
 * Absent `from`/`to` ⇒ the server defaults to the last full month.
 */
export function useCampaignReconciliation(
  storeId: string,
  from?: string,
  to?: string,
  enabled = true,
) {
  return tsr.campaigns.reconciliation.useQuery({
    queryKey: queryKeys.campaignReconciliation(storeId, from, to),
    queryData: { params: { id: storeId }, query: { ...(from ? { from } : {}), ...(to ? { to } : {}) } },
    enabled,
  });
}

/**
 * Read the campaign TERMS axis for a store over a date range
 * (clarity-campaign-terms-economics Req 5.1): the discount buckets (each a
 * distinct economic signature — percentage, min, cap, audience, item-scope) with
 * their order count + discount, plus the store-period `summary` (discounted
 * orders, discount total, tied %, unexplained). Absent `from`/`to` ⇒ the server
 * defaults to the last full month; empty-range safe. Mirrors
 * {@link useCampaignReconciliation}. Used by the Terms tab (task 6.2).
 */
export function useCampaignTerms(
  storeId: string,
  from?: string,
  to?: string,
  enabled = true,
) {
  return tsr.campaigns.getCampaignTerms.useQuery({
    queryKey: queryKeys.campaignTerms(storeId, from, to),
    queryData: { params: { id: storeId }, query: { ...(from ? { from } : {}), ...(to ? { to } : {}) } },
    enabled,
  });
}

/**
 * Read ONE terms bucket's DETAIL over a date range + page
 * (clarity-campaign-terms-economics Req 5.1, task 9.3): the bucket's declared
 * signature, a roll-up `summary` over ALL its orders, the observed-% `histogram`
 * (+ the `declBin` the declared rate lands in), and a PAGINATED newest-first
 * `orders` slice (`page`/`pageSize`/`total`). Drives the "VIEW · TERM DETAIL"
 * page. `bucketKey` is the signature `key` from the terms list (carries `%|:`
 * separators) — the CALLER passes it DECODED; ts-rest URL-encodes the path param
 * on the wire, so there is no double-encoding here.
 *
 * Mirrors {@link useCampaignTerms} with the bucket key + pagination folded into
 * the query key so each page caches independently (a "Load more" fetches a
 * sibling page without clobbering the first). Absent `from`/`to` ⇒ the server
 * spans the full ingested range; absent `page`/`pageSize` ⇒ the core defaults
 * (page 1, pageSize 50). `enabled` gates the fetch while the surface is gated.
 */
export function useTermBucketDetail(
  storeId: string,
  bucketKey: string,
  from?: string,
  to?: string,
  page?: number,
  pageSize?: number,
  enabled = true,
) {
  return tsr.campaigns.getTermBucketDetail.useQuery({
    queryKey: queryKeys.termBucketDetail(storeId, bucketKey, from, to, page, pageSize),
    queryData: {
      params: { id: storeId, bucketKey },
      query: {
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
        ...(page ? { page } : {}),
        ...(pageSize ? { pageSize } : {}),
      },
    },
    enabled,
  });
}

/**
 * Read ONE item-scoped campaign's DEAL ECONOMICS over a date range
 * (clarity-campaign-terms-economics Req 5.2): the per-scoped-item food-cost
 * margin report (base price, food cost %, food cost, discount given with a cap
 * marker, margin/order) + the avg summary. Reporting only — never a match gate.
 * A non-item-scoped campaign resolves to an empty `items`. Absent `from`/`to` ⇒
 * the server defaults to the last full month. Mirrors
 * {@link useCampaignReconciliation}'s signature with the campaign id folded into
 * the key. Used by the deal-economics panel (task 6.3).
 */
export function useDealEconomics(
  storeId: string,
  campaignId: string,
  from?: string,
  to?: string,
  enabled = true,
) {
  return tsr.campaigns.getItemScopedDealEconomics.useQuery({
    queryKey: queryKeys.dealEconomics(storeId, campaignId, from, to),
    queryData: {
      params: { id: storeId, cid: campaignId },
      query: { ...(from ? { from } : {}), ...(to ? { to } : {}) },
    },
    enabled,
  });
}

export function useReconciliationByPayout(storeId: string, payoutId: string, enabled = true) {
  return tsr.reconciliation.byPayout.useQuery({
    queryKey: queryKeys.reconciliationByPayout(storeId, payoutId),
    queryData: { params: { id: storeId, payoutId } },
    enabled,
  });
}

/** Latest reconciliation report for a period window; `404` ⇒ not reconciled yet. */
export function useReconciliationByPeriod(
  storeId: string,
  periodStart: string,
  periodEnd: string,
  enabled = true,
) {
  return tsr.reconciliation.byPeriod.useQuery({
    queryKey: queryKeys.reconciliationByPeriod(storeId, periodStart, periodEnd),
    queryData: { params: { id: storeId }, query: { periodStart, periodEnd } },
    enabled,
  });
}

/**
 * Step 2 — fetch the resolved catalog for review (food cost / base / platform
 * price; Req 2.2). A PURE READ (unified-data-gathering Req 4.1, 4.2): the
 * catalog — menu items, synced options, and orders-driven provisioned items —
 * was already pulled + persisted up front by the leading `Data_Gathering_Step`,
 * so this reads the persisted current catalog with NO first-visit orders
 * provisioning. The server's `getCatalog` no longer pulls or accepts a
 * `provision` param, so none is sent. Edits still POST effective-dated versions.
 */
export function useOnboardingCatalog(storeId: string, enabled = true) {
  return tsr.onboarding.getCatalog.useQuery({
    queryKey: queryKeys.onboardingCatalog(storeId),
    queryData: { params: { id: storeId }, query: {} },
    enabled,
  });
}

/**
 * Onboarding Campaigns step — read the scoped-candidate deals + the item-pick
 * catalog for setting a selected-items deal's `scopedProductIds`
 * (clarity-onboarding-campaign-scope Req 3.3). A PURE READ (ingestion-single-source):
 * the server reads persisted campaigns + the current catalog and classifies each
 * candidate — NO pull. The `200` body is `{ candidates, catalog }`; a not-yet-ready
 * store answers `409` (`onboarding_required`), surfaced on `error`. `enabled` gates
 * the fetch while the step is not active.
 */
export function useCampaignScope(storeId: string, enabled = true) {
  return tsr.onboarding.getCampaignScope.useQuery({
    queryKey: queryKeys.campaignScope(storeId),
    queryData: { params: { id: storeId } },
    enabled,
  });
}

/**
 * Step 3 — poll the live reference-pull progress while deriving
 * (clarity-derive-progress Req 4.1, 4.5). Mirrors {@link useConnections}'s
 * `refetchInterval` pattern but at a tight ~1s cadence (a cheap in-memory read),
 * gated on `enabled` (the caller passes `derive.isPending`) so the poll stops the
 * instant the mutation resolves (Req 4.4). `data` is the `{ status, body }`
 * union; callers narrow on `status === 200` to reach the `DeriveProgress` body.
 */
export function useDeriveProgress(storeId: string, enabled: boolean) {
  return tsr.onboarding.getDeriveProgress.useQuery({
    queryKey: queryKeys.deriveProgress(storeId),
    queryData: { params: { id: storeId } },
    enabled,
    refetchInterval: 1000,
  });
}

/**
 * Step 3 — read the derived summary RECOMPUTED from the store's persisted raw
 * records (no portal re-pull). This is what makes reopening onboarding instant:
 * the Step-3 view reads this first and renders immediately when a summary exists.
 * `body` is `null` when no reference data has been ingested yet (the UI shows the
 * "Run derivation" button). `enabled` lets the caller skip the read for a store
 * that clearly has nothing yet.
 */
export function useDerivedSummary(storeId: string, enabled = true) {
  return tsr.onboarding.getDerived.useQuery({
    queryKey: queryKeys.derivedSummary(storeId),
    queryData: { params: { id: storeId } },
    enabled,
  });
}

/**
 * After a catalog write, DROP the cached derived summary (not just mark it
 * stale) — catalog-coverage Req 6.5. `getDerived` re-costs from the current
 * catalog, so the cached summary is now known-wrong; an invalidate alone would
 * let Step 3 (which is unmounted while the owner edits in Step 2) render those
 * pre-edit numbers on mount while it refetches. A reset clears the data, so
 * Step 3 shows its loading state until the recomputed summary lands; a mounted
 * observer (if any) refetches immediately.
 */
function resetDerivedSummary(
  queryClient: Pick<QueryClient, 'resetQueries'>,
  storeId: string,
): Promise<void> {
  return queryClient.resetQueries({ queryKey: queryKeys.derivedSummary(storeId) });
}

// ── Mutations (invalidate on success) ────────────────────────────────────────

/** Authenticate (phone + password); the caller stores `{ token, user }` in the session. */
export function useLogin() {
  return tsr.auth.login.useMutation();
}

/** Request a WhatsApp verification code for a phone (clarity-workspace-users). */
export function useRequestVerify() {
  return tsr.auth.requestVerify.useMutation();
}

/** Verify a phone with its code; returns a fresh session on success. */
export function useVerifyPhone() {
  return tsr.auth.verifyPhone.useMutation();
}

/** Request a WhatsApp password-reset code for a phone. */
export function useRequestReset() {
  return tsr.auth.requestReset.useMutation();
}

/** Complete a password reset with the code + a new password; returns a session. */
export function useResetPassword() {
  return tsr.auth.resetPassword.useMutation();
}

// ── Connection status (clarity-acquisition Req 5.2; clarity-platform-abstraction Req 4.1, 5.4) ──

/**
 * Admin multi-platform connection rollup for the shell indicator + the admin
 * Connections surface (clarity-platform-abstraction Req 4.1, 5.4). Reads the
 * admin-only `GET /admin/connections`, which returns the live status of EVERY
 * live platform as an array (reserved platforms excluded server-side via
 * `livePlatforms()`); each entry carries only `{ platform, status, checkedAt }`
 * — no session material.
 *
 * Polls on an interval so a human re-login in a running Chrome is reflected
 * without a manual refresh (recovery is automatic server-side; the UI just
 * re-reads). The probe touches the live browser session, so it's deliberately
 * not aggressive. Replaces the old single-platform `useTalabatConnection`.
 */
export function useConnections() {
  return tsr.connections.adminConnections.useQuery({
    queryKey: ['connections', 'all'],
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
}

/**
 * Admin-only write of a MyThings store's credential (mythings-connections) —
 * `PUT /admin/connections/mythings/stores/:storeId/credentials`. The body is
 * `{ accountLogin, password }`; the server seals it (reversible encryption),
 * re-probes the store's signin, and returns the refreshed per-store `StoreStatus`
 * — it NEVER reads a credential back. On success we invalidate the connections
 * read so the store's card flips to its new state without a manual refresh.
 */
export function usePutMythingsStoreCredentials() {
  const queryClient = tsr.useQueryClient();
  return tsr.connections.putMythingsStoreCredentials.useMutation({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['connections', 'all'] });
    },
  });
}

// ── Admin ingestion control (admin-ingestion-control Req 5.2, 4.2, 4.6) ──────

/** How often the run-history read re-polls while a batch is in flight. */
const INGESTION_POLL_MS = 2000;

/**
 * Read the admin ingestion run history (`GET /admin/ingestion/runs`), newest
 * first, with optional `storeId`/`batchId`/`status`/`limit` filters (Req 3.1,
 * 3.2). Admin-only server-side; a `client` principal gets `403` (surfaced on
 * `error`), the route guard being the backstop for the hidden nav entry.
 *
 * Polling (Req 5.2): mirror {@link useDeriveProgress}'s `refetchInterval` pattern
 * but gate the poll on the DATA rather than a mutation's pending flag — poll
 * while ANY row in the latest list is `running`, and STOP (`refetchInterval:
 * false`) once none are. TanStack v5 passes the live `query` to the
 * `refetchInterval` callback; `query.state.data` is the ts-rest `{ status, body }`
 * union, so we narrow on `status === 200` before reading the typed `IngestionRun[]`
 * body. A pressed "Run now" / "Re-run" therefore re-polls automatically until the
 * batch finishes, then settles without a manual refresh.
 */
export function useIngestionRuns(filters?: ListRunsQuery) {
  return tsr.adminIngestion.listRuns.useQuery({
    queryKey: queryKeys.ingestionRuns(filters),
    queryData: { query: filters ?? {} },
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.status !== 200) return false;
      return data.body.some((run) => run.status === 'running') ? INGESTION_POLL_MS : false;
    },
  });
}

/**
 * Trigger an asynchronous ingestion batch (`POST /admin/ingestion/runs`; Req 4.1,
 * 4.2). The body is `{ storeIds?, mode? }` — omit `storeIds` for ALL enabled
 * stores ("Run now (all)") or pass one id for a per-store "Re-run", with `mode`
 * `daily` (incremental) or `backfill` (full-window). The `202` body is `{
 * batchId, storeCount }`; the work runs in the background and its outcome is read
 * from the run history (Req 4.6).
 *
 * `onSuccess` invalidates every cached run-history list so the panel re-reads and
 * the new batch's `running` rows appear — which in turn re-arms
 * {@link useIngestionRuns}'s poll until the batch finishes. The partial key
 * (`['admin', 'ingestion', 'runs']`) matches all filter variants.
 */
export function useTriggerIngestion() {
  const queryClient = tsr.useQueryClient();
  return tsr.adminIngestion.triggerRun.useMutation({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'ingestion', 'runs'] });
    },
  });
}

/**
 * Admin RE-RUN of a failed onboarding GATHER (`reference` run) by store id —
 * `POST /stores/:id/onboarding/gather`, the SAME edge the onboarding wizard and
 * the auto-gather-on-attach fire (`startReferenceGather`). This is the correct
 * retry for a failed `reference` row: a gather runs BEFORE a store is enabled,
 * whereas the admin daily/backfill trigger only pulls `enabled` stores (so
 * re-triggering a failed gather there just records a `skipped` "store not
 * enabled" row — the mismatch this hook fixes). Admin-scoped: `getById` resolves
 * every tenant's store for an admin, so an admin can re-gather any branch. On
 * success the run-history list is invalidated so the new `reference` run paints.
 */
export function useRetryGather() {
  const queryClient = tsr.useQueryClient();
  return tsr.onboarding.gather.useMutation({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'ingestion', 'runs'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.stores });
    },
  });
}

// ── Admin monthly-offer review (clarity-campaign-matching Req 3.3, 5.2, 10.3) ──
//
// The two admin-only writes behind the MonthlyOfferReview surface. Both are
// role-gated server-side (a `client` principal gets 403; the hidden nav + route
// guard are the convenience). Neither is store-scoped — the monthly offer is
// shared across the platform's stores — so there is no per-store cache to
// invalidate here; the UI consumes each mutation's own returned body (the OCR
// draft, then the persisted campaigns/occurrences + warnings).

/**
 * Upload a monthly-offer image → OCR DRAFT (`POST
 * /admin/campaigns/monthly-offer/ocr`; Req 3.1, 3.2). The admin picks a platform
 * and an offer-sheet image (sent as `{ platformKey, imageBase64, mimeType }`);
 * the server runs the key-gated Gemini OCR seam and returns `{ provider, draft
 * }`. With no OCR key configured the `provider` is `'noop'` and `draft.offers` is
 * `[]` — a clean-empty draft the admin fills in by hand (never an error). The
 * surface reads this mutation's `data` directly to seed the editable review form.
 */
export function useUploadMonthlyOffer() {
  return tsr.campaigns.uploadMonthlyOffer.useMutation();
}

/**
 * List the CURRENT platform-shared monthly offers + occurrences for a platform
 * (`GET /admin/campaigns/monthly-offer?platformKey=…`; admin-only server-side;
 * Req 3.4, 10.3). Powers the "Current offers" catalog on the review screen so an
 * admin sees what's already saved instead of an empty uploader. Re-keyed by
 * `platformKey` so switching the platform picker refetches.
 */
export function useMonthlyOffers(platformKey: string, enabled = true) {
  return tsr.campaigns.listMonthlyOffers.useQuery({
    queryKey: ['admin', 'monthly-offers', platformKey] as const,
    queryData: { query: { platformKey: platformKey as 'talabat' | 'careem' } },
    enabled,
  });
}

/**
 * Save the reviewed monthly offers (`POST /admin/campaigns/monthly-offer`; Req
 * 3.3, 3.4, 5.2). The admin submits the corrected `{ platformKey, draft }` — the
 * draft carrying the per-offer `platform_deal_key` tags for monthly↔platform
 * dedupe (Req 5.2); the server normalizes it via the core `talabatMonthlyOffer`
 * adapter into `Campaign[]` + occurrences (`provenance:'admin'`, shared across
 * the platform's stores, effective-dated — append, never rewrite) and returns `{
 * campaigns, occurrences, warnings }`. A malformed offer/occurrence is skipped
 * with a warning, never a throw. The surface renders the persisted result + any
 * warnings from this mutation's `data`.
 */
export function useSaveMonthlyOffer() {
  return tsr.campaigns.saveMonthlyOffer.useMutation();
}

/**
 * INTERNAL dev backtest: replay our payout estimate against settled payouts
 * across all stores (`GET /admin/payout-backtest`; admin-only server-side). The
 * surface that mounts this is DEV-build-only. `200.body` carries per-store groups
 * + the server's default tolerance; the page recomputes pass/fail at a chosen
 * tolerance client-side.
 */
export function usePayoutBacktest() {
  return tsr.payoutBacktest.list.useQuery({
    queryKey: ['admin', 'payout-backtest'] as const,
  });
}

/** The query key for the admin payout-schedule LIST (so a save can invalidate it). */
const payoutSchedulesKey = ['admin', 'payout-schedules'] as const;

/**
 * List every CURRENT payout schedule across platforms + months (`GET
 * /admin/payout-schedules`; clarity-payout-schedule Req 2.4). Admin-only
 * server-side; drives the admin list surface. `200.body` is the array of
 * `PayoutSchedule` (empty when none configured), newest month first.
 */
export function usePayoutSchedules() {
  return tsr.payoutSchedule.listSchedules.useQuery({
    queryKey: payoutSchedulesKey,
  });
}

/**
 * Read the admin-entered payout schedule for a platform + month (`GET
 * /admin/payout-schedule`; clarity-payout-schedule Req 2.1). Admin-only
 * server-side; the surface loads the saved schedule so the admin edits/corrects
 * rather than re-enters. `200.body` is the `PayoutSchedule` or `null` when none
 * is saved. `enabled` gates the fetch until a platform + month is chosen.
 */
export function usePayoutSchedule(platformKey: string, month: string, enabled = true) {
  return tsr.payoutSchedule.getSchedule.useQuery({
    queryKey: ['admin', 'payout-schedule', platformKey, month] as const,
    queryData: { query: { platformKey: platformKey as 'talabat' | 'careem', month } },
    enabled,
  });
}

/**
 * Save a platform + month payout schedule (`POST /admin/payout-schedule`;
 * clarity-payout-schedule Req 2.2). The admin submits `{ platformKey, month, rows
 * }`; the server validates (skip-with-warning), persists shared across the
 * platform's stores effective-dated (append, never rewrite), and returns `{
 * schedule, warnings }`. On success the read is invalidated so the surface shows
 * the persisted version.
 */
export function useSavePayoutSchedule() {
  const queryClient = tsr.useQueryClient();
  return tsr.payoutSchedule.saveSchedule.useMutation({
    onSuccess: (res) => {
      if (res.status !== 200) return;
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'payout-schedule', res.body.schedule.platformKey, res.body.schedule.month],
      });
      // The list surface shows this schedule's row — refresh it too.
      void queryClient.invalidateQueries({ queryKey: payoutSchedulesKey });
    },
  });
}

// ── Banner options (clarity-banner-options Req 4.1, 6.1) ─────────────────────
//
// Admin-managed, platform-scoped paid placements. The admin routes are
// role-gated server-side (403 for a `client`). Every derived field
// (`displayState`, `group`, `isNew`, `openRuns`, `newCount`) comes from the
// server's clock; web renders it as-is. Any admin write can change what every
// store on that platform sees, so each mutation invalidates the admin list AND
// every store's banner-options read.

function invalidateBannerOptions(queryClient: Pick<QueryClient, 'invalidateQueries'>): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.bannerOptionsAdmin() });
  void queryClient.invalidateQueries({
    predicate: (query) => isStoreBannerOptionsKey(query.queryKey),
  });
}

/** Admin: every banner option across platforms, each with its `displayState` (Req 4.1). */
export function useAdminBannerOptions(enabled = true) {
  return tsr.bannerOptions.listAdmin.useQuery({
    queryKey: queryKeys.bannerOptionsAdmin(),
    enabled,
  });
}

/** Admin: create a banner option as a draft or published (Req 2.1–2.5). */
export function useCreateBannerOption() {
  const queryClient = tsr.useQueryClient();
  return tsr.bannerOptions.create.useMutation({
    onSuccess: () => invalidateBannerOptions(queryClient),
  });
}

/** Admin: replace a banner option by id, `{ params: { bid }, body }` (Req 2.6). */
export function useUpdateBannerOption() {
  const queryClient = tsr.useQueryClient();
  return tsr.bannerOptions.update.useMutation({
    onSuccess: () => invalidateBannerOptions(queryClient),
  });
}

/** Admin: publish or move to drafts, `{ params: { bid }, body: { status } }` (Req 3.4). */
export function useSetBannerOptionStatus() {
  const queryClient = tsr.useQueryClient();
  return tsr.bannerOptions.setStatus.useMutation({
    onSuccess: () => invalidateBannerOptions(queryClient),
  });
}

/** Admin: delete a banner option, `{ params: { bid } }` (Req 2.7). */
export function useRemoveBannerOption() {
  const queryClient = tsr.useQueryClient();
  return tsr.bannerOptions.remove.useMutation({
    onSuccess: () => invalidateBannerOptions(queryClient),
  });
}

/**
 * Owner: the published, not-yet-ended banner options for the store's platform,
 * upcoming first, plus `newCount` (Req 6.1–6.3, 8.1). A pure read of persisted
 * options; tenant-scoped server-side (404 for a store the caller can't see).
 * `enabled` gates the fetch until a single store is in scope.
 */
export function useStoreBannerOptions(storeId: string, enabled = true) {
  return tsr.bannerOptions.listForStore.useQuery({
    queryKey: queryKeys.storeBannerOptions(storeId),
    queryData: { params: { id: storeId } },
    enabled,
  });
}

// ── Locations (clarity-locations) ─────────────────────────────────────────────
//
// Admin-only. The page loads ONE read per client (locations + flagged stores +
// suggestions); the three mutations invalidate that read.

function invalidateLocations(
  queryClient: Pick<QueryClient, 'invalidateQueries'>,
  clientId: string,
): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.clientLocationsView(clientId) });
}

/** Admin: a client's locations + its stores (flagged) + suggested links. */
export function useClientLocationsView(clientId: string, enabled = true) {
  return tsr.locations.view.useQuery({
    queryKey: queryKeys.clientLocationsView(clientId),
    queryData: { params: { clientId } },
    enabled,
  });
}

/** Admin: create a location grouping the client's stores. */
export function useCreateLocation(clientId: string) {
  const queryClient = tsr.useQueryClient();
  return tsr.locations.create.useMutation({
    onSuccess: () => invalidateLocations(queryClient, clientId),
  });
}

/** Admin: update a location (name / Arabic / members, full replace). */
export function useUpdateLocation(clientId: string) {
  const queryClient = tsr.useQueryClient();
  return tsr.locations.update.useMutation({
    onSuccess: () => invalidateLocations(queryClient, clientId),
  });
}

/** Admin: delete a location; its stores become loose. */
export function useDeleteLocation(clientId: string) {
  const queryClient = tsr.useQueryClient();
  return tsr.locations.remove.useMutation({
    onSuccess: () => invalidateLocations(queryClient, clientId),
  });
}

// ── Monthly campaign review (clarity-monthly-campaign-review) ─────────────────
//
// Admin PUBLISH is per (platform, month): the status read + the publish
// mutation (change-only fan-out). The owner READS the recomputed review + AM
// brief per (store, month). Publishing invalidates the status so the banner
// flips to "published".

/** Admin: the draft/published status + enabled-store count for a platform's month. */
export function useMonthlyReviewPublishStatus(
  platformKey: 'talabat' | 'careem' | 'mythings',
  month: string,
  enabled = true,
) {
  return tsr.monthlyReview.publishStatus.useQuery({
    queryKey: queryKeys.monthlyReviewPublishStatus(platformKey, month),
    queryData: { params: { platformKey, month } },
    enabled,
  });
}

/** Admin: build + send the month's review to every enabled store's owner. */
export function usePublishMonthlyReview(
  platformKey: 'talabat' | 'careem' | 'mythings',
  month: string,
) {
  const queryClient = tsr.useQueryClient();
  return tsr.monthlyReview.publish.useMutation({
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.monthlyReviewPublishStatus(platformKey, month),
      });
    },
  });
}

/** Owner/admin: the recomputed monthly review for a store + published month (404 when unpublished). */
export function useMonthlyReview(storeId: string, month: string, enabled = true) {
  return tsr.monthlyReview.getReview.useQuery({
    queryKey: queryKeys.monthlyReview(storeId, month),
    queryData: { params: { id: storeId, month } },
    enabled,
  });
}

/** Owner/admin: the AM brief for a store + published month, in the given locale. */
export function useAmBrief(
  storeId: string,
  month: string,
  locale: 'en' | 'ar',
  enabled = true,
) {
  return tsr.monthlyReview.getBrief.useQuery({
    queryKey: queryKeys.monthlyReviewBrief(storeId, month, locale),
    queryData: { params: { id: storeId, month }, query: { locale } },
    enabled,
  });
}

// ── Advisor chat (clarity-advisor-chat) ───────────────────────────────────────
//
// The owner's one web conversation with the advisor. `thread` opens/reads it;
// `send` posts a message and gets the advisor's inline reply (the server answers
// with the same engine WhatsApp uses); the panel then refreshes the thread.

/**
 * Open/read the caller's web advisor conversation for the client in scope. For a
 * client that's their own tenant; for an admin it's `clientId` (the client
 * workspace they have open), sent as a query so the server answers for that
 * tenant. `enabled` gates it until a client is in scope.
 */
export function useAdvisorThread(clientId: string | null, enabled = true) {
  return tsr.advisor.thread.useQuery({
    queryKey: queryKeys.advisorThread(clientId),
    queryData: clientId ? { query: { clientId } } : {},
    enabled,
    // Poll while the panel is open so a HUMAN agent's replies (merged in from
    // Chatwoot after a takeover) and the human-handled state surface live
    // without the customer having to send another message.
    refetchInterval: 4000,
  });
}

/** Send a message to the advisor; invalidates the thread so the reply shows. */
export function useSendAdvisorMessage(clientId: string | null) {
  const queryClient = tsr.useQueryClient();
  return tsr.advisor.send.useMutation({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.advisorThread(clientId) });
      // A free-form answer meters AI cost against the workspace, so refresh the
      // usage meter after every send (clarity-advisor-llm Req 4.4).
      void queryClient.invalidateQueries({ queryKey: queryKeys.advisorUsage(clientId) });
    },
  });
}

/**
 * The caller's workspace AI usage this month vs its cap (clarity-advisor-llm Req
 * 4.4). Owner read-only; for an admin it's the client workspace they have open
 * (sent as a query). `enabled` gates it until a client is in scope. The panel
 * shows the remaining budget and, when exhausted, why the free-form answer is
 * blocked (the deterministic chips still work).
 */
export function useMyAdvisorUsage(clientId: string | null, enabled = true) {
  return tsr.advisor.myUsage.useQuery({
    queryKey: queryKeys.advisorUsage(clientId),
    queryData: clientId ? { query: { clientId } } : {},
    enabled,
  });
}

/**
 * Admin read of a workspace's month-to-date AI spend + effective cap
 * (clarity-advisor-llm Req 4.1). The `clientId` is a PATH param (the admin names
 * the workspace), unlike the owner `myUsage` read which sends it as a query.
 * Keyed separately so it doesn't share the owner meter's cache entry.
 */
export function useAdminAdvisorUsage(clientId: string, enabled = true) {
  return tsr.advisor.getUsage.useQuery({
    queryKey: queryKeys.advisorUsageAdmin(clientId),
    queryData: { params: { clientId } },
    enabled,
  });
}

/**
 * Set or clear a workspace's AI cap override (admin only; clarity-advisor-llm
 * Req 4.2). `body.capUsd` a number sets the override (raising a limit by
 * agreement); `null` clears it, reverting to the global default. On success
 * refreshes BOTH the admin read AND the owner meter (same workspace), so an
 * owner's panel reflects a raised cap without a manual reload.
 */
export function useSetAdvisorCap(clientId: string) {
  const queryClient = tsr.useQueryClient();
  return tsr.advisor.setCap.useMutation({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.advisorUsageAdmin(clientId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.advisorUsage(clientId) });
    },
  });
}

// ── Workspace users (clarity-workspace-users) ─────────────────────────────────
//
// A workspace (= client) may have many user logins. An admin manages any
// workspace; a client manages its OWN (self-serve). All three are scoped by the
// `:clientId` path param; the server authorizes (admin → any; client → own).

/** List a workspace's users (admin, or the workspace's own client). */
export function useWorkspaceUsers(clientId: string, enabled = true) {
  return tsr.workspaceUsers.list.useQuery({
    queryKey: queryKeys.workspaceUsers(clientId),
    queryData: { params: { clientId } },
    enabled,
  });
}

/** Add a user to a workspace; refreshes the list on success. */
export function useAddWorkspaceUser(clientId: string) {
  const queryClient = tsr.useQueryClient();
  return tsr.workspaceUsers.create.useMutation({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.workspaceUsers(clientId) });
    },
  });
}

/** Remove a user from a workspace; refreshes the list on success. */
export function useRemoveWorkspaceUser(clientId: string) {
  const queryClient = tsr.useQueryClient();
  return tsr.workspaceUsers.remove.useMutation({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.workspaceUsers(clientId) });
    },
  });
}

/**
 * Update one platform's payment-gateway fee defaults (admin only server-side);
 * refreshes the defaults list so the settings page + onboarding seeds reflect it.
 */
export function useUpdatePlatformFeeDefaults() {
  const queryClient = tsr.useQueryClient();
  return tsr.settings.updateFeeDefaults.useMutation({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.platformFeeDefaults });
    },
  });
}

/** Create a client (admin only server-side); refreshes the clients list. */
export function useCreateClient() {
  const queryClient = tsr.useQueryClient();
  return tsr.clients.create.useMutation({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.clients });
    },
  });
}

/** Create a brand under a client; refreshes brand lists. */
export function useCreateBrand() {
  const queryClient = tsr.useQueryClient();
  return tsr.brands.create.useMutation({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.brands });
    },
  });
}

/**
 * Discover the platform outlets for the linked account (auto store discovery).
 * Manual-trigger (not auto-run): it touches the live browser session, so the
 * operator presses "Discover" rather than probing the portal on every render.
 */
export function useDiscoverStores(platformKey: string, enabled: boolean) {
  return tsr.stores.discover.useQuery({
    queryKey: ['stores', 'discover', platformKey],
    queryData: { query: { platformKey: platformKey as 'talabat' | 'careem' } },
    enabled,
    staleTime: 0,
    gcTime: 0,
  });
}

/**
 * Discover the platform CHAINS (as brands) + their outlets for the linked
 * account, to import under a target client (brand-model R4). Manual-trigger
 * (touches the live browser session), so `enabled` gates the fetch.
 */
export function useDiscoverBrands(platformKey: string, clientId: string, enabled: boolean) {
  return tsr.brands.discover.useQuery({
    queryKey: ['brands', 'discover', platformKey, clientId],
    queryData: { query: { platformKey: platformKey as 'talabat' | 'careem', clientId } },
    enabled,
    staleTime: 0,
    gcTime: 0,
  });
}

/** Import a discovered brand + its branches under a client; refreshes brands + stores. */
export function useImportBrand() {
  const queryClient = tsr.useQueryClient();
  return tsr.brands.import.useMutation({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.brands });
      void queryClient.invalidateQueries({ queryKey: queryKeys.stores });
    },
  });
}

/**
 * Manually add a MyThings brand + its branches (mythings-manual-add) — the only
 * path a MyThings store enters Clarity. On success refreshes brands + stores AND
 * the connections rollup (['connections','all']), since the new per-store
 * statuses appear there immediately.
 */
export function useAddMythingsStore() {
  const queryClient = tsr.useQueryClient();
  return tsr.brands.addMythings.useMutation({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.brands });
      void queryClient.invalidateQueries({ queryKey: queryKeys.stores });
      void queryClient.invalidateQueries({ queryKey: ['connections', 'all'] });
    },
  });
}

/** Create (= assign) a store under a brand; lands it in state `assigned`. */
export function useCreateStore() {
  const queryClient = tsr.useQueryClient();
  return tsr.stores.create.useMutation({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.stores });
    },
  });
}

/** Enable a store (guarded server-side); refreshes store reads. */
export function useEnableStore() {
  const queryClient = tsr.useQueryClient();
  return tsr.stores.enable.useMutation({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.stores });
    },
  });
}

/** Disable a store (stop ingestion + arm grace); refreshes store reads. */
export function useDisableStore() {
  const queryClient = tsr.useQueryClient();
  return tsr.stores.disable.useMutation({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.stores });
    },
  });
}

/**
 * Set a branch's restaurant category + sub-categories (campaign applicability;
 * admin only). Refreshes store reads so the Campaigns overview reflects the new
 * category. Re-matching persisted orders is a separate admin/ops step.
 */
export function useSetStoreCategory() {
  const queryClient = tsr.useQueryClient();
  return tsr.stores.setCategory.useMutation({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.stores });
    },
  });
}

/**
 * Unassign a never-enabled branch (reverse of assign; `assigned → removed`).
 * Refreshes store reads so the branch drops out of the brand's branch list.
 */
export function useUnassignStore() {
  const queryClient = tsr.useQueryClient();
  return tsr.stores.unassign.useMutation({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.stores });
    },
  });
}

/**
 * Hard-delete a REMOVED branch (admin only; legal only from `removed`). Drops
 * the row entirely so a re-import recreates the branch fresh. Refreshes store
 * reads so the branch disappears from the brand's branch list.
 */
export function useRemoveStore() {
  const queryClient = tsr.useQueryClient();
  return tsr.stores.remove.useMutation({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.stores });
    },
  });
}

// ── Onboarding wizard mutations (clarity-onboarding-ingestion Req 2.1–2.3) ────

/**
 * Step 1 — set markup + default food cost (+ confirm payment-gateway params).
 * Refreshes the single store read so the wizard sees the updated profile.
 */
export function useOnboardingStep1(storeId: string) {
  const queryClient = tsr.useQueryClient();
  return tsr.onboarding.step1.useMutation({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.store(storeId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.onboardingCatalog(storeId) });
    },
  });
}

/**
 * Step 2 — persist per-item food-cost / base / platform-price edits (a partial
 * list is fine: items not in `edits` keep their values) and option-row fan-out
 * edits (`optionEdits`, keyed by `optionKey`; defaults to `[]`, so item-only
 * callers omit it — clarity-catalog-options Req 7.1). Refreshes the catalog
 * review — `items` AND the `options` rows (Req 6.8) — AND the Step-3 derived
 * summary: `getDerived` recomputes from the persisted current catalog, so a
 * newly priced item or option must re-cost there (catalog-coverage Req 4.6, 6.5).
 *
 * The catalog refetch is AWAITED (returned from `onSuccess`), so the mutation
 * stays pending until the refreshed review is in the cache: a per-row confirm's
 * "Saving…" state holds until the row has actually moved groups, and callers'
 * own `onSuccess` sees the fresh review.
 */
export function useSaveOnboardingCatalog(storeId: string) {
  const queryClient = tsr.useQueryClient();
  return tsr.onboarding.saveCatalog.useMutation({
    onSuccess: () => {
      // Not awaited: a mounted summary elsewhere recomputes in the background.
      void resetDerivedSummary(queryClient, storeId);
      return queryClient.invalidateQueries({ queryKey: queryKeys.onboardingCatalog(storeId) });
    },
  });
}

/**
 * Onboarding Campaigns step — set a selected-items deal's `scopedProductIds`
 * (clarity-onboarding-campaign-scope Req 3.3). Persists an append-only campaign
 * version server-side (`PUT /stores/:id/onboarding/campaign-scope`, body `{
 * campaignId, scopedProductIds }`); the `200` body is `{ campaign }`. On success
 * it invalidates the scope read so the candidate's current chips / set-unset
 * status refresh without a manual refetch.
 */
export function useSetCampaignScope(storeId: string) {
  const queryClient = tsr.useQueryClient();
  return tsr.onboarding.setCampaignScope.useMutation({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.campaignScope(storeId) });
    },
  });
}

/**
 * RECALCULATE (onboarding-automation Req 2) — re-derive the food-cost / economic
 * summary from the store's PERSISTED data after the catalog was confirmed or
 * edited, so the owner can explicitly refresh the numbers against the catalog
 * they just confirmed. A PURE read of persisted data server-side (no re-pull);
 * it also re-persists the derived profile fields onto the store.
 *
 * On success the server returns the freshly recomputed `DerivedRead` (or `null`
 * when nothing is ingested yet). Seed the derived-summary cache with that body
 * so the refreshed figures render immediately, then invalidate so any mounted
 * reader converges on the canonical read.
 */
export function useRecalculate(storeId: string) {
  const queryClient = tsr.useQueryClient();
  return tsr.onboarding.recalculate.useMutation({
    onSuccess: (res) => {
      if (res.status !== 200) return;
      // Seed the derived-summary cache with the fresh recompute so the figures
      // update without waiting for a refetch. ts-rest query data is the full
      // `{ status, body }` envelope the `useQuery` hook returns.
      queryClient.setQueryData(queryKeys.derivedSummary(storeId), {
        status: 200,
        body: res.body,
      });
      return queryClient.invalidateQueries({ queryKey: queryKeys.derivedSummary(storeId) });
    },
  });
}

/**
 * Step 2 — merge a source catalog item into a canonical item (the source's name
 * + id + aliases become aliases of the target; catalog-item-aliases Req 2.8).
 * Returns the refreshed `CatalogReview` (200) or a `409` alias conflict (ts-rest
 * rejects it onto `error` as `{ status, body }`; read it with `errorStatus`).
 *
 * Only a 200 refreshes anything: a 409 persisted nothing, so the cache (and the
 * open picker + its surfaced conflict message) stays untouched. On success the
 * catalog refetch is AWAITED so callers' own `onSuccess` sees the fresh review,
 * and the derived summary is dropped — the merge changes which item order lines
 * resolve to, so the persisted recompute must re-cost.
 */
export function useMergeCatalog(storeId: string) {
  const queryClient = tsr.useQueryClient();
  return tsr.onboarding.mergeCatalog.useMutation({
    onSuccess: (res) => {
      if (res.status !== 200) return;
      void resetDerivedSummary(queryClient, storeId);
      return queryClient.invalidateQueries({ queryKey: queryKeys.onboardingCatalog(storeId) });
    },
  });
}

/**
 * Step 2 — remove an alias from a canonical item, restoring it as a separate
 * (archived) catalog item (catalog-item-aliases Req 5.4). Same cache behavior
 * as {@link useMergeCatalog}: 200 ⇒ awaited catalog refetch + derived-summary
 * reset; `409` (restore would collide) leaves the cache untouched.
 */
export function useUnmergeCatalog(storeId: string) {
  const queryClient = tsr.useQueryClient();
  return tsr.onboarding.unmergeCatalog.useMutation({
    onSuccess: (res) => {
      if (res.status !== 200) return;
      void resetDerivedSummary(queryClient, storeId);
      return queryClient.invalidateQueries({ queryKey: queryKeys.onboardingCatalog(storeId) });
    },
  });
}

/**
 * Back-dated food-cost edit (clarity-reconciliation Req 11.4–11.6): set a new
 * food-cost value AND an effective date → the server splits the covering
 * effective-dated version (append-only) and may flip an archived item to active.
 * Refreshes the catalog review so the badge/rate reflect the write — including
 * the `options` rows, whose inherited rates cascade from the edited item
 * (clarity-catalog-options Req 6.8) — and the derived summary so Step 3's
 * persisted recompute re-costs with the new rate.
 */
export function useEditCatalogFoodCost(storeId: string) {
  const queryClient = tsr.useQueryClient();
  return tsr.onboarding.editCatalogItemFoodCost.useMutation({
    onSuccess: (res) => {
      // Only refresh the catalog on an applied edit (200). A rejected effective
      // date (409) changed nothing — leave the cache (and the open editor + its
      // surfaced conflict message) untouched.
      if (res.status === 200) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.onboardingCatalog(storeId) });
        // The new version re-costs orders in the persisted recompute (Req 4.6, 6.5).
        void resetDerivedSummary(queryClient, storeId);
      }
    },
  });
}

/**
 * Data_Gathering_Step — the LEADING onboarding step: pull + persist the whole
 * reference window up front (`POST /stores/:id/onboarding/gather`;
 * unified-data-gathering Req 3.1–3.4). Mirrors {@link useOnboardingStep1} /
 * {@link useDeriveProfile}: on success it invalidates the single store read so
 * the server-derived `gatherRunning` / `gatherBatchId` (Req 8.3, 8.5) and the
 * reference-data flags refresh, and the ingested-data lists that the gather just
 * populated. The `200` body is the {@link OnboardingGatherResult}: `ok` +
 * `failedSources` (a disconnected session is reported here, never a throw and
 * never an upload fallback — Req 3.5), the per-source `persisted` counts, and —
 * on the dedup path (task 7.5) — `alreadyRunning` with the existing running
 * run's `runId` / `batchId` so the client resumes into the live loading state
 * rather than starting a second gather (Req 8.2, 8.3).
 */
export function useGather(storeId: string) {
  const queryClient = tsr.useQueryClient();
  return tsr.onboarding.gather.useMutation({
    onSuccess: () => {
      // The server derives `gatherRunning`/`gatherBatchId` + the reference-data
      // flags from `ingestion_runs` at read time, so re-read the store to resume
      // into the right step (Req 8.3, 8.5).
      void queryClient.invalidateQueries({ queryKey: queryKeys.store(storeId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.stores });
      // The gather persisted a fresh reference window across every source, so the
      // later read-only steps/pages must re-read it.
      void queryClient.invalidateQueries({ queryKey: queryKeys.orders(storeId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.statements(storeId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.payouts(storeId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.onboardingCatalog(storeId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.derivedSummary(storeId) });
    },
  });
}

/**
 * Step 3 — reference pull + derive the read-only profile; completing it sets the
 * onboarding gate. Refreshes the store read + ingested-data lists that unblock
 * once onboarding completes.
 */
export function useDeriveProfile(storeId: string) {
  const queryClient = tsr.useQueryClient();
  return tsr.onboarding.derive.useMutation({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.store(storeId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.stores });
      void queryClient.invalidateQueries({ queryKey: queryKeys.orders(storeId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.statements(storeId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.payouts(storeId) });
      // The re-pull persisted fresh raw records → the recomputed summary changes.
      void queryClient.invalidateQueries({ queryKey: queryKeys.derivedSummary(storeId) });
    },
  });
}

/** Run the ingestion orchestrator for a store (sync); refreshes ingested-data lists. */
export function useSyncStore(storeId: string) {
  const queryClient = tsr.useQueryClient();
  return tsr.sync.sync.useMutation({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.orders(storeId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.statements(storeId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.payouts(storeId) });
    },
  });
}

// ── MyThings owner-entered payouts + expenses (clarity-balance-coverage) ─────
//
// The owner records what MyThings actually paid them and any costs they
// incurred, so the MyThings balance estimate reconciles earned − paid − costs.
// Owner/tenant-scoped server-side. Every write invalidates BOTH the entry list
// AND the store's derived summary (the balance card reads the estimate off the
// summary, so it must refresh when an entry changes).

/** List a MyThings store's owner-reported payouts. */
export function useMythingsPayouts(storeId: string, enabled = true) {
  return tsr.mythingsManual.listPayouts.useQuery({
    queryKey: queryKeys.mythingsPayouts(storeId),
    queryData: { params: { id: storeId } },
    enabled,
  });
}

/** List a MyThings store's owner-entered expenses. */
export function useMythingsExpenses(storeId: string, enabled = true) {
  return tsr.mythingsManual.listExpenses.useQuery({
    queryKey: queryKeys.mythingsExpenses(storeId),
    queryData: { params: { id: storeId } },
    enabled,
  });
}

/** Invalidate a MyThings store's entry list + its derived summary after a write. */
function invalidateMythings(queryClient: ReturnType<typeof tsr.useQueryClient>, storeId: string, which: 'payouts' | 'expenses') {
  void queryClient.invalidateQueries({
    queryKey: which === 'payouts' ? queryKeys.mythingsPayouts(storeId) : queryKeys.mythingsExpenses(storeId),
  });
  void queryClient.invalidateQueries({ queryKey: queryKeys.derivedSummary(storeId) });
}

export function useCreateMythingsPayout(storeId: string) {
  const queryClient = tsr.useQueryClient();
  return tsr.mythingsManual.createPayout.useMutation({
    onSuccess: (res) => {
      if (res.status !== 201) return;
      invalidateMythings(queryClient, storeId, 'payouts');
    },
  });
}

export function useDeleteMythingsPayout(storeId: string) {
  const queryClient = tsr.useQueryClient();
  return tsr.mythingsManual.deletePayout.useMutation({
    onSuccess: (res) => {
      if (res.status !== 204) return;
      invalidateMythings(queryClient, storeId, 'payouts');
    },
  });
}

export function useCreateMythingsExpense(storeId: string) {
  const queryClient = tsr.useQueryClient();
  return tsr.mythingsManual.createExpense.useMutation({
    onSuccess: (res) => {
      if (res.status !== 201) return;
      invalidateMythings(queryClient, storeId, 'expenses');
    },
  });
}

export function useDeleteMythingsExpense(storeId: string) {
  const queryClient = tsr.useQueryClient();
  return tsr.mythingsManual.deleteExpense.useMutation({
    onSuccess: (res) => {
      if (res.status !== 204) return;
      invalidateMythings(queryClient, storeId, 'expenses');
    },
  });
}

// ── Per-store analytics (clarity-owner-analytics) ────────────────────────────
//
// Owner-maintained ratings / delivery-time / opening-hours / conversion records
// + the server-computed new-vs-returning split and deal incrementality. Owner/
// tenant-scoped server-side. An upsert/delete invalidates the list (and the
// dashboard reads it fresh).

/** List a store's owner-maintained analytics records. */
export function useStoreAnalytics(storeId: string, enabled = true) {
  return tsr.storeAnalytics.list.useQuery({
    queryKey: queryKeys.storeAnalytics(storeId),
    queryData: { params: { id: storeId } },
    enabled,
  });
}

export function useUpsertStoreAnalytics(storeId: string) {
  const queryClient = tsr.useQueryClient();
  return tsr.storeAnalytics.upsert.useMutation({
    onSuccess: (res) => {
      if (res.status !== 200) return;
      void queryClient.invalidateQueries({ queryKey: queryKeys.storeAnalytics(storeId) });
    },
  });
}

export function useDeleteStoreAnalytics(storeId: string) {
  const queryClient = tsr.useQueryClient();
  return tsr.storeAnalytics.remove.useMutation({
    onSuccess: (res) => {
      if (res.status !== 204) return;
      void queryClient.invalidateQueries({ queryKey: queryKeys.storeAnalytics(storeId) });
    },
  });
}

/** The new-vs-returning split for a store over `[from, to)` (computed server-side). */
export function useCustomerSplit(storeId: string, from: string, to: string, enabled = true) {
  return tsr.storeAnalytics.customerSplit.useQuery({
    queryKey: queryKeys.customerSplit(storeId, from, to),
    queryData: { params: { id: storeId }, query: { from, to } },
    enabled,
  });
}

/** Per-deal incrementality estimate for a store over `[from, to)`. */
export function useDealIncrementality(storeId: string, from: string, to: string, enabled = true) {
  return tsr.storeAnalytics.dealIncrementality.useQuery({
    queryKey: queryKeys.dealIncrementality(storeId, from, to),
    queryData: { params: { id: storeId }, query: { from, to } },
    enabled,
  });
}
